import type { JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';

interface ConfirmRevokeShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  pending: boolean;
}

/**
 * Revoke confirmation for a share link. Revoking kills the link immediately —
 * anyone holding it gets a 404 — so it needs an explicit confirm. Uses
 * AppDialog (not window.confirm, which Teams suppresses).
 */
export function ConfirmRevokeShareDialog({
  open,
  onOpenChange,
  onConfirm,
  pending,
}: ConfirmRevokeShareDialogProps): JSX.Element {
  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Revoke share link?"
      description="This cannot be undone."
    >
      <div className="mt-3 flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          Revoking this link stops it working immediately. Anyone who already
          has it will no longer be able to open the snapshot.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive-outline"
            size="sm"
            disabled={pending}
            onClick={onConfirm}
          >
            {pending ? 'Revoking…' : 'Revoke link'}
          </Button>
        </div>
      </div>
    </AppDialog>
  );
}
